import { useRef, useState, useEffect } from 'react';
import Header from '../Header/Header';
import Menu from '../Menu/Menu';
import EventModal from './EventModal/EventModal';
import { APP_EVENTS } from '../../types/museum/museum';
import Ambassador from '../Ambassador/Ambassador';
import { useThreeJSApp } from '../../hooks/useThreeJSApp';
import { useMuseumEvents } from '../../hooks/useMuseumEvents';
import MuseumDescription from './MuseumDescription/MuseumDescription';
import { epochsData } from '../../EpochsData/Epochs';
import { useTransition } from '../../contexts/TransitionContext';
import { useMusic } from '../../contexts/MusicContext';
import { TimelineEvent } from '../Timeline/Timeline';

interface MuseumProps {
  isSceneLoaded: boolean;
  setIsSceneLoaded: (value: boolean) => void;
  onActiveEventChange?: (event: TimelineEvent | null) => void;
}

const Museum: React.FC<MuseumProps> = ({ 
  isSceneLoaded, 
  setIsSceneLoaded,
  onActiveEventChange 
}) => {
  const { setIsSceneLoaded: setIsSceneLoadedTransition } = useTransition();
  const { fadeAndNavigate } = useMusic();
  const containerRef = useRef<HTMLDivElement>(null);
  const ambassadorRef = useRef<{ toggleVisibility: () => void }>();
  const [showDescription, setShowDescription] = useState(false);
  const [epoch, setEpoch] = useState('youtubism');
  const app = useThreeJSApp(containerRef);
  const params = new URLSearchParams(window.location.search);
  const target = params.get('target')?.toLowerCase() || '';
  const eventParam = params.get('event');

  if (target === '' || target === 'null') {
    fadeAndNavigate('/');
  }

  useEffect(() => {
    setEpoch(target || 'youtubism');
  }, [epoch]);

  useEffect(() => {
    if (eventParam && app) {
      const event = epochsData[epoch].events.find(e => e.title === eventParam);
      if (event) {
        setSelectedEvent(event);
        setIsModalOpen(true);
      }
    }
  }, [app, epoch, eventParam]);

  useEffect(() => {
    setIsSceneLoaded(false);

    return () => {
      if (app && app.appEventsBus) {
        Object.values(APP_EVENTS).forEach(eventName => {
          app.appEventsBus.emit(eventName, null);
        });
      }
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, []);

  const {
    progress,
    activeEvent,
    isModalOpen,
    selectedEvent,
    setIsModalOpen,
    setSelectedEvent
  } = useMuseumEvents(app, epochsData[epoch].events);

  const setProgressInThreeJS = (targetProgress: number) => {
    if (app && app.appEventsBus) {
      app.appEventsBus.emit(APP_EVENTS.SET_PROGRESS, targetProgress);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
    onActiveEventChange?.(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('event');
    window.history.pushState({}, '', url);
  };

  const handleAmbassadorToggle = () => {
    if (ambassadorRef.current?.toggleVisibility) {
      ambassadorRef.current.toggleVisibility();
    }
  };

  const handleAmbassadorVisibilityChange = (toggleVisibility: () => void) => {
    ambassadorRef.current = { toggleVisibility };
  };

  const handleInfoToggle = () => {
    setShowDescription(prev => !prev);
  };

  return (
    <div>
      <MuseumDescription
        title={'Welcome to ' + epochsData[epoch].info.title}
        period={epochsData[epoch].info.period}
        description={epochsData[epoch].info.description}
        isVisible={showDescription}
        onClose={() => setShowDescription(false)}
      />
      <Header
        activeEvent={activeEvent}
      />
      <div className="app app-museum" ref={containerRef} id="app" style={{ width: '100%', height: '100vh' }}>
      </div>
      <Menu
        progress={progress}
        events={epochsData[epoch].events}
        setProgressInThreeJS={setProgressInThreeJS}
        onAmbassadorToggle={handleAmbassadorToggle}
        onInfoToggle={handleInfoToggle}
        epochTitle={epochsData[epoch].info.title}
        ambassadorName={epochsData[epoch].ambassador.name}
      />

      {selectedEvent && (
        <EventModal
          event={selectedEvent}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onEventOpen={(event) => onActiveEventChange?.(event)}
          onEventClose={() => onActiveEventChange?.(null)}
        />
      )}
      <Ambassador
        name={epochsData[epoch].ambassador.name}
        description={epochsData[epoch].ambassador.description}
        image={epochsData[epoch].ambassador.image}
        socialLinks={epochsData[epoch].ambassador.socialLinks}
        activeEvent={activeEvent}
        events={epochsData[epoch].events}
        onVisibilityChange={handleAmbassadorVisibilityChange}
        showDescription={showDescription}
        position={epochsData[epoch].ambassador.position}
      />
    </div>
  );
};

export default Museum; 